/**
 * Crossfade layer-stack bookkeeping shared by `<ChapterScene>` (the small
 * cover-slot illustration) and `<ChapterSceneFullscreen>` (its full-screen
 * view) — both must animate the same way when the image on screen changes,
 * so this is factored out of `ChapterScene.tsx` rather than duplicated.
 * Not pure (it's a hook, backed by React state/effects/rAF), which is why
 * it lives here rather than in `lib/chapterScenes.ts` alongside the actual
 * "which image" decision.
 */
import { useEffect, useRef, useState } from "react";

export const CROSSFADE_MS = 600;

export interface CrossfadeLayer {
  /** `null` = the story cover layer, otherwise the chapter image src. */
  key: string | null;
  entered: boolean;
}

/**
 * Stacks a new (initially invisible) layer whenever `targetKey` changes,
 * flips it to visible on the next frame so the opacity transition actually
 * has something to animate from, then drops every layer under it once the
 * transition has finished. Returns the layers to render, topmost last.
 */
export function useChapterSceneLayers(targetKey: string | null): CrossfadeLayer[] {
  const [layers, setLayers] = useState<CrossfadeLayer[]>(() => [{ key: targetKey, entered: true }]);
  const prevKeyRef = useRef(targetKey);

  // A new target (chapter boundary crossed during normal playback, or a
  // manual seek landing in a different chapter) stacks a new layer on top,
  // starting invisible — see the effect below for the opacity flip that
  // actually animates it in.
  useEffect(() => {
    if (targetKey === prevKeyRef.current) return;
    prevKeyRef.current = targetKey;
    setLayers((prev) => [...prev, { key: targetKey, entered: false }]);
  }, [targetKey]);

  // Flip the newest layer's opacity on the next frame, not the same one it
  // mounted in — CSS can't transition a property whose starting value was
  // only just set in the same paint.
  useEffect(() => {
    const top = layers[layers.length - 1];
    if (!top || top.entered) return;
    const raf = requestAnimationFrame(() => {
      setLayers((prev) => prev.map((l, i) => (i === prev.length - 1 ? { ...l, entered: true } : l)));
    });
    return () => cancelAnimationFrame(raf);
  }, [layers]);

  // Once the crossfade has finished, the new top layer fully covers
  // whatever is under it, so older layers can be dropped — nothing else
  // needs a fade-out, only the newest layer ever needs to animate at all.
  useEffect(() => {
    if (layers.length <= 1) return;
    const top = layers[layers.length - 1];
    if (!top.entered) return;
    const id = window.setTimeout(() => {
      setLayers((prev) => (prev.length > 1 ? [prev[prev.length - 1]] : prev));
    }, CROSSFADE_MS + 50);
    return () => window.clearTimeout(id);
  }, [layers]);

  return layers;
}
